import { Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';

import { useDeckCheckQuery, useProfileMeQuery } from '@/services/qbotApi';
import { DeckGrid } from '@/components/cr/DeckGrid';
import { RatingBar } from '@/components/cr/RatingBar';
import type { QbotApiError } from '@/types/qbot';

function isQbotApiError(err: unknown): err is QbotApiError {
  return typeof err === 'object' && err !== null && 'data' in err;
}

/**
 * Deck-check screen — ATK/DEF/SYN/VER/BAL rating + tips for the linked
 * player's current deck. Tag resolution is implicit: use the linked
 * SavedPlayer; if absent, redirect to /cr/link.
 *
 * Calls /profile/me (already cached after the home loads) to read the
 * current deck, then /decks/check/:tag for the rating. Could be folded
 * into a single response if it ever becomes a perf concern; for now it
 * keeps the cards endpoint cacheable independently of the rating.
 */
export function CRDeckCheck() {
  const { t } = useTranslation('cr');
  const { data: profile, isLoading: profileLoading, error: profileError } =
    useProfileMeQuery();
  const tag = profile?.savedTag ?? profile?.player?.tag?.replace('#', '') ?? '';
  const { data, isLoading, error } = useDeckCheckQuery({ tag }, { skip: !tag });

  if (profileLoading) {
    return <div className="px-4 py-6 text-sm text-fg-muted">{t('deckCheck.loading')}</div>;
  }
  if (profileError) {
    const code = isQbotApiError(profileError) ? profileError.data?.error?.code : undefined;
    if (code === 'NOT_LINKED' || code === 'NOT_FOUND') return <Navigate to="/cr/link" replace />;
  }
  if (isLoading || !data) {
    return <div className="px-4 py-6 text-sm text-fg-muted">{t('deckCheck.loading')}</div>;
  }
  if (error) {
    return <div className="px-4 py-6 text-sm text-rose-300">{t('deckCheck.error')}</div>;
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto px-4 py-4">
      <header>
        <h1 className="text-xl font-bold text-fg">{t('deckCheck.title')}</h1>
        <p className="text-[11px] text-fg-muted">
          {data.archetype} · {t('deckCheck.avgElixir', { value: data.avgElixir.toFixed(1) })}
        </p>
      </header>

      <section className="rounded-xl bg-bg-secondary p-3">
        <DeckGrid cards={data.deck} avgElixir={data.avgElixir} />
      </section>

      <section className="flex flex-col gap-2 rounded-xl bg-bg-secondary p-3">
        <h2 className="text-xs font-bold uppercase tracking-wider text-fg-muted">
          {t('deckCheck.rating')}
        </h2>
        <RatingBar label={t('deckCheck.label.atk')} value={data.rating.attack} />
        <RatingBar label={t('deckCheck.label.def')} value={data.rating.defense} />
        <RatingBar label={t('deckCheck.label.syn')} value={data.rating.synergy} />
        <RatingBar label={t('deckCheck.label.ver')} value={data.rating.versatility} />
        <RatingBar label={t('deckCheck.label.bal')} value={data.rating.balance} />
      </section>

      {data.tips.length > 0 && (
        <section className="flex flex-col gap-2 rounded-xl bg-bg-secondary p-3">
          <h2 className="text-xs font-bold uppercase tracking-wider text-fg-muted">
            {t('deckCheck.tips')}
          </h2>
          <ul className="flex flex-col gap-1">
            {data.tips.map((tip) => (
              <li key={tip} className="text-sm text-fg">
                • {tip}
              </li>
            ))}
          </ul>
        </section>
      )}

      <Link
        to="/cr/coach"
        className="mt-auto flex items-center justify-center gap-2 rounded-lg bg-brand py-3 text-sm font-bold uppercase tracking-wide text-brand-fg transition-opacity active:opacity-80"
      >
        <Sparkles className="h-4 w-4" />
        {t('deckCheck.askAi')}
      </Link>
    </div>
  );
}
